import React from 'react'
import { Container } from '@mui/material'
import {
    AccountBalance, Apartment, ArrowForward, Autorenew, AutoAwesome, BarChart, Campaign,
    FindInPage, Groups, Hub, Insights, Payments, Psychology, School, SmartToy,
    SupportAgent, VerifiedUser
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Header from '../Components/Header'
import Footer from '../Components/Footer'
import TopHeader from '../Components/TopHeader'
import TitrePage from './TitrePage'
import Reveal from '../Components/ui/Reveal'
import SectionHeader from '../Components/ui/SectionHeader'
import CtaBand from '../Components/ui/CtaBand'
import Waves from '../Components/ui/Waves'
import mark from '../Files/IBG Logo single  white.svg'
import markDark from '../Files/IBG Logo.svg'
import heroBg from '../Data/arriere-plan-texture-hexadecimal-pour-mise-reseau.png'

const HUB_SATELLITES = [
    { key: 'hubData', x: 260, y: 58 },
    { key: 'hubTeams', x: 448, y: 178 },
    { key: 'hubProcesses', x: 392, y: 422 },
    { key: 'hubTools', x: 128, y: 422 },
    { key: 'hubClients', x: 72, y: 178 },
];

const EXP_ICONS = [
    <Psychology />, <AutoAwesome />, <Hub />, <Autorenew />, <School />,
    <Groups />, <Insights />, <SmartToy />, <VerifiedUser />,
];

const UC_ICONS = [
    <Payments />, <AccountBalance />, <VerifiedUser />, <Groups />, <SupportAgent />,
    <Campaign />, <Apartment />, <FindInPage />, <BarChart />, <Autorenew />,
];

function FocusAI() {
    const { t } = useTranslation();
    const navigate = useNavigate()
    const isDark = document.documentElement.dataset.theme === 'dark';

    const go = (link: string) => {
        navigate(link)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    const scrollTo = (id: string) => document.querySelector(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })

    const expertises = [1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => ({
        icon: EXP_ICONS[n - 1],
        title: t(`FocusAI.e${n}Title`),
        text: t(`FocusAI.e${n}Text`),
    }))
    const approach = [1, 2, 3, 4, 5, 6, 7].map((n) => ({
        title: t(`FocusAI.a${n}Title`),
        text: t(`FocusAI.a${n}Text`),
    }))
    const useCases = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({
        icon: UC_ICONS[n - 1],
        label: t(`FocusAI.uc${n}`),
    }))
    const trainings = [1, 2, 3, 4, 5, 6].map((n) => ({
        title: t(`FocusAI.t${n}Title`),
        text: t(`FocusAI.t${n}Text`),
        level: t(`FocusAI.t${n}Level`),
    }))
    const journey = [1, 2, 3, 4, 5, 6, 7].map((n) => t(`FocusAI.j${n}`))
    const tech = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => t(`FocusAI.tech${n}`))
    const cases = [1, 2, 3].map((n) => ({
        title: t(`FocusAI.c${n}Title`),
        text: t(`FocusAI.c${n}Text`),
        tag: t(`FocusAI.c${n}Tag`),
    }))
    const pillars = [1, 2, 3].map((n) => ({
        title: t(`FocusAI.whyP${n}Title`),
        text: t(`FocusAI.whyP${n}Text`),
    }))

    return (
        <div className="ibg-page ibg-page--focus">
            <TitrePage hiddeTitle titre={"titrePage.FocusAI.titre"} lien='focusia' pageDescription={'titrePage.FocusAI.description'} keyWords={'titrePage.FocusAI.keywords'} />
            <TopHeader />
            <Header />

            <section className="ibg-hero fai-hero">
                <Waves />
                <img className="ibg-hero__media" src={heroBg} alt="" />
                <div className="ibg-hero__veil" />
                <Container maxWidth="lg" className="fai-hero__inner">
                    <div className="fai-hero__content">
                        <Reveal>
                            <span className="ibg-eyebrow ibg-eyebrow--light">{t("FocusAI.eyebrow")}</span>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <h1 className="ibg-display">{t("FocusAI.heroTitle")}</h1>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <p className="ibg-hero__lead">{t("FocusAI.heroLead")}</p>
                        </Reveal>
                        <Reveal delay={0.24} className="ibg-actions">
                            <button className="ibg-btn ibg-btn--light" onClick={() => go('/contact')}>
                                {t("FocusAI.ctaTransform")} <ArrowForward fontSize="small" />
                            </button>
                            <button className="ibg-btn ibg-btn--outline-light" onClick={() => scrollTo('#expertises')}>
                                {t("FocusAI.ctaSolutions")}
                            </button>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <span className="fai-hero__brand"><img src={isDark ? mark : markDark} alt="" /> {t("FocusAI.brand")}</span>
                        </Reveal>
                    </div>
                    <Reveal delay={0.2} className="fai-hero__visual">
                        <svg className="fai-hub" viewBox="0 0 520 520" aria-hidden="true">
                            <circle className="fai-hub__ring" cx="260" cy="260" r="122" />
                            <circle className="fai-hub__ring fai-hub__ring--outer" cx="260" cy="260" r="202" />
                            {HUB_SATELLITES.map((s) => (
                                <line key={s.key} className="fai-hub__link" x1="260" y1="260" x2={s.x} y2={s.y} />
                            ))}
                            {HUB_SATELLITES.map((s, i) => (
                                <circle key={s.key} className="fai-hub__pulse" r="3.5">
                                    <animateMotion dur={`${5 + i * 0.8}s`} repeatCount="indefinite" path={`M260 260 L${s.x} ${s.y}`} />
                                </circle>
                            ))}
                            {HUB_SATELLITES.map((s) => (
                                <g key={s.key} className="fai-hub__node">
                                    <circle cx={s.x} cy={s.y} r="30" />
                                    <text x={s.x} y={s.y + 50} textAnchor="middle">{t(`FocusAI.${s.key}`)}</text>
                                </g>
                            ))}
                            <g className="fai-hub__core">
                                <circle className="fai-hub__glow" cx="260" cy="260" r="62" />
                                <circle cx="260" cy="260" r="42" />
                                <text x="260" y="268" textAnchor="middle">AI</text>
                            </g>
                        </svg>
                    </Reveal>
                </Container>
            </section>

            <section className="ibg-section">
                <Container maxWidth="lg">
                    <div className="ibg-split ibg-split--center">
                        <div className="ibg-split__text">
                            <SectionHeader eyebrow={t("FocusAI.whyEyebrow")} title={t("FocusAI.whyTitle")} />
                            <Reveal delay={0.1}>
                                <p className="ibg-body">{t("FocusAI.whyText")}</p>
                                <p className="ibg-body">{t("FocusAI.whyText2")}</p>
                            </Reveal>
                        </div>
                        <Reveal delay={0.15} className="ibg-split__media">
                            <div className="fai-pillars">
                                {pillars.map((p, i) => (
                                    <div key={p.title} className="fai-pillar">
                                        <span className="fai-pillar__index">{String(i + 1).padStart(2, '0')}</span>
                                        <div>
                                            <h3 className="ibg-h4">{p.title}</h3>
                                            <p className="ibg-small">{p.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </Container>
            </section>

            <section id="expertises" className="ibg-section ibg-section--surface">
                <Container maxWidth="lg">
                    <SectionHeader
                        eyebrow={t("FocusAI.expEyebrow")}
                        title={t("FocusAI.expTitle")}
                        description={t("FocusAI.expLead")}
                    />
                    <div className="fai-grid">
                        {expertises.map((e, index) => (
                            <Reveal key={e.title} delay={index * 0.05} className="fai-card">
                                <span className="fai-card__icon">{e.icon}</span>
                                <h3 className="ibg-h4">{e.title}</h3>
                                <p className="ibg-small">{e.text}</p>
                            </Reveal>
                        ))}
                    </div>
                </Container>
            </section>

            <section className="ibg-section">
                <Container maxWidth="lg">
                    <SectionHeader
                        eyebrow={t("FocusAI.approachEyebrow")}
                        title={t("FocusAI.approachTitle")}
                        description={t("FocusAI.approachLead")}
                    />
                    <ol className="fai-steps">
                        {approach.map((s, index) => (
                            <Reveal key={s.title} delay={index * 0.05} className="fai-step">
                                <li>
                                    <span className="fai-step__index">{String(index + 1).padStart(2, '0')}</span>
                                    <h3 className="ibg-h4">{s.title}</h3>
                                    <p className="ibg-small">{s.text}</p>
                                </li>
                            </Reveal>
                        ))}
                    </ol>
                </Container>
            </section>

            <section className="ibg-section ibg-section--surface">
                <Container maxWidth="lg">
                    <SectionHeader
                        eyebrow={t("FocusAI.ucEyebrow")}
                        title={t("FocusAI.ucTitle")}
                        description={t("FocusAI.ucLead")}
                    />
                    <div className="fai-uc">
                        {useCases.map((u, index) => (
                            <Reveal key={u.label} delay={index * 0.04} className="fai-uc__item">
                                <span className="fai-uc__icon">{u.icon}</span>
                                <span>{u.label}</span>
                            </Reveal>
                        ))}
                    </div>
                </Container>
            </section>

            <section className="ibg-section">
                <Container maxWidth="lg">
                    <SectionHeader
                        eyebrow={t("FocusAI.trainEyebrow")}
                        title={t("FocusAI.trainTitle")}
                        description={t("FocusAI.trainLead")}
                    />
                    <div className="fai-grid">
                        {trainings.map((tr, index) => (
                            <Reveal key={tr.title} delay={index * 0.05} className="fai-card fai-card--train">
                                <span className="fai-card__level">{tr.level}</span>
                                <span className="fai-card__num">{String(index + 1).padStart(2, '0')}</span>
                                <h3 className="ibg-h4">{tr.title}</h3>
                                <p className="ibg-small">{tr.text}</p>
                            </Reveal>
                        ))}
                    </div>
                </Container>
            </section>

            <section className="ibg-section ibg-section--dark">
                <Waves />
                <Container maxWidth="lg">
                    <SectionHeader
                        inverted
                        eyebrow={t("FocusAI.aifEyebrow")}
                        title={t("FocusAI.aifTitle")}
                        description={t("FocusAI.aifText")}
                    />
                    <Reveal delay={0.1}>
                        <div className="fai-journey">
                            {journey.map((j, i) => (
                                <React.Fragment key={j}>
                                    <span className={`fai-journey__step${i === journey.length - 1 ? ' is-final' : ''}`} style={{ '--j': i } as React.CSSProperties}>
                                        <em>{String(i + 1).padStart(2, '0')}</em>{j}
                                    </span>
                                    {i < journey.length - 1 && <span className="fai-journey__arrow"><ArrowForward fontSize="small" /></span>}
                                </React.Fragment>
                            ))}
                        </div>
                    </Reveal>
                </Container>
            </section>

            <section className="ibg-section ibg-section--surface">
                <Container maxWidth="lg">
                    <SectionHeader
                        eyebrow={t("FocusAI.techEyebrow")}
                        title={t("FocusAI.techTitle")}
                        description={t("FocusAI.techLead")}
                    />
                    <Reveal>
                        <div className="fai-tech">
                            {tech.map((name) => <span key={name} className="fai-tech__chip">{name}</span>)}
                        </div>
                    </Reveal>
                </Container>
            </section>

            <section className="ibg-section">
                <Container maxWidth="lg">
                    <SectionHeader
                        eyebrow={t("FocusAI.casesEyebrow")}
                        title={t("FocusAI.casesTitle")}
                        description={t("FocusAI.casesLead")}
                    />
                    <div className="ibg-grid ibg-grid--3">
                        {cases.map((c, index) => (
                            <Reveal key={c.title} delay={index * 0.08} className="fai-case">
                                <span className="fai-case__tag">{c.tag}</span>
                                <h3 className="ibg-h4">{c.title}</h3>
                                <p className="ibg-small">{c.text}</p>
                            </Reveal>
                        ))}
                    </div>
                </Container>
            </section>

            <CtaBand title={t("FocusAI.ctaTitle")} text={t("FocusAI.ctaText")}>
                <button className="ibg-btn ibg-btn--light" onClick={() => go('/contact')}>
                    {t("FocusAI.ctaBtn")} <ArrowForward fontSize="small" />
                </button>
                <button className="ibg-btn ibg-btn--outline-light" onClick={() => go('/focusia/chat')}>
                    <AutoAwesome fontSize="small" /> {t("FocusAI.chatCta")}
                </button>
            </CtaBand>

            <Footer />
        </div>
    )
}

export default FocusAI
